import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Youtube, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONTACT_INFO, PRACTICE_AREAS } from '../constants/contact';

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [practiceDropdownOpen, setPracticeDropdownOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-[#fdfcfb]/95 backdrop-blur-md border-b border-neutral-200">
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-baseline gap-1.5">
            <span className="font-heading text-2xl font-semibold text-primary-700">BELTAS</span>
            <span className="font-subheading text-lg tracking-wider text-neutral-500">LAW</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Practice Areas Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setPracticeDropdownOpen(true)}
              onMouseLeave={() => setPracticeDropdownOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 font-subheading font-medium text-neutral-700 hover:text-primary-600 transition-colors"
                aria-expanded={practiceDropdownOpen}
                aria-haspopup="true"
              >
                Practice Areas
                <ChevronDown className={`w-4 h-4 transition-transform ${practiceDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {practiceDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-neutral-200 py-2 overflow-hidden"
                  >
                    {PRACTICE_AREAS.map((area) => (
                      <Link
                        key={area.href}
                        to={area.href}
                        className={`block px-4 py-3 font-subheading text-sm hover:bg-primary-50 transition-colors ${
                          isActive(area.href) ? 'text-primary-600 bg-primary-50' : 'text-neutral-700'
                        }`}
                      >
                        {area.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/notable-cases"
              className={`font-subheading font-medium transition-colors flex items-center gap-1 ${
                isActive('/notable-cases') ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Notable Cases
            </Link>

            <Link
              to="/videos"
              className={`font-subheading font-medium transition-colors flex items-center gap-1 ${
                isActive('/videos') ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'
              }`}
            >
              <Youtube className="w-4 h-4" />
              Videos
            </Link>

            <Link
              to="/about"
              className={`font-subheading font-medium transition-colors ${
                isActive('/about') ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'
              }`}
            >
              About
            </Link>

            <Link
              to="/contact"
              className={`font-subheading font-medium transition-colors ${
                isActive('/contact') ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'
              }`}
            >
              Contact
            </Link>

            {/* Phone Number */}
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="flex items-center gap-2 font-subheading font-medium text-neutral-600 hover:text-primary-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              {CONTACT_INFO.phoneDisplay}
            </a>

            {/* CTA Button */}
            <Link to="/contact" className="btn-secondary">
              Initial Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 text-neutral-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-neutral-200 overflow-hidden"
          >
            <div className="container-main py-6 space-y-4">
              {/* Phone - prominent on mobile */}
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center justify-center gap-2 py-3 bg-primary-50 rounded-lg font-subheading font-semibold text-primary-700"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phoneDisplay}
              </a>

              <div className="border-t border-neutral-200 pt-4">
                <p className="font-subheading text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                  Practice Areas
                </p>
                {PRACTICE_AREAS.map((area) => (
                  <Link
                    key={area.href}
                    to={area.href}
                    className="block py-3 font-subheading text-neutral-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {area.name}
                  </Link>
                ))}
              </div>

              <div className="border-t border-neutral-200 pt-4 space-y-3">
                <Link
                  to="/notable-cases"
                  className="flex items-center gap-2 py-2 font-subheading text-neutral-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <BookOpen className="w-4 h-4 text-primary-600" />
                  Notable Cases
                </Link>
                <Link
                  to="/videos"
                  className="flex items-center gap-2 py-2 font-subheading text-neutral-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Youtube className="w-4 h-4 text-red-600" />
                  Videos
                </Link>
                <Link
                  to="/about"
                  className="block py-2 font-subheading text-neutral-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  className="block py-2 font-subheading text-neutral-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </div>

              <Link
                to="/contact"
                className="btn-secondary w-full justify-center mt-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Schedule Initial Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
